/**
 * IES LM-63 parser. Logic ported from taman-ml/taman/util/ies/parse.
 */
import { LdcParsed } from './ldcTypes';

function asNum(v: string): number {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function tokenizeLines(lines: string[]): string[] {
  const tokens: string[] = [];
  for (const line of lines) {
    const t = line.trim().toLowerCase();
    if (t === 'end') break;
    for (const part of line.trim().split(/\s+/)) {
      for (const token of part.split(',')) {
        const s = token.trim();
        if (s) tokens.push(s);
      }
    }
  }
  return tokens;
}

/**
 * Detect IES format from text content: IES files always contain a `TILT=` line
 * (and usually start with an `IESNA` header).
 */
export function isIesText(text: string): boolean {
  return /(^|\n)\s*TILT=/i.test(text) || /^\s*IESNA/i.test(text);
}

/**
 * Parse IES file text (body after TILT= line). Returns config and intensity matrix I[horizontal][vertical].
 */
export function parseIES(text: string): LdcParsed {
  const normalized = text.replace(/\r\n/g, '\n');
  const tiltMatch = normalized.match(/(^|\n)TILT=.*(\n|$)/i);
  if (!tiltMatch || tiltMatch.index === undefined) {
    throw new Error('Invalid IES: missing TILT= anchor');
  }
  const tiltLineStart = tiltMatch.index + (tiltMatch[1] ? tiltMatch[1].length : 0);
  const tiltLineEnd = normalized.indexOf('\n', tiltLineStart);
  const body = tiltLineEnd >= 0 ? normalized.slice(tiltLineEnd + 1) : '';
  const lines = body.split(/\n/);
  const tokens = tokenizeLines(lines);
  let i = 0;
  const next = () => (i < tokens.length ? asNum(tokens[i++]) : 0);

  next(); // lamp_num
  next(); // lumens_per_lamp
  const candelaMultiplier = next();
  const verticalAnglesNumRead = Math.max(0, Math.floor(next()));
  const horizontalAnglesNumRead = Math.max(0, Math.floor(next()));
  next(); // photometric_type
  next(); // units_type
  next(); // width
  next(); // length
  next(); // height
  next(); // ballast_data
  next(); // ballast_lamp_photometric_factor
  next(); // input_watts

  const verticalAngles: number[] = [];
  for (let k = 0; k < verticalAnglesNumRead; k++) verticalAngles.push(next());
  const horizontalAngles: number[] = [];
  for (let k = 0; k < horizontalAnglesNumRead; k++) horizontalAngles.push(next());

  const nH = horizontalAnglesNumRead || 1;
  const nV = verticalAnglesNumRead || 1;
  const I: number[][] = [];
  for (let ih = 0; ih < nH; ih++) {
    const row: number[] = [];
    for (let iv = 0; iv < nV; iv++) row.push(next());
    I.push(row);
  }

  return {
    verticalAnglesNum: verticalAnglesNumRead,
    horizontalAnglesNum: horizontalAnglesNumRead,
    candelaMultiplier,
    verticalAngles,
    horizontalAngles,
    I,
  };
}
